import React, { useState, useRef, useEffect } from 'react'
import { Feature } from '../../api/client'

interface EnumListEditorProps {
    feature: Feature
    values: Array<{
        id?: string
        value: string
    }>
    onChange: (values: Array<{ id?: string; value: string }>) => void
    onClose: () => void
}

/**
 * Apple-style commit semantics: append, delete, and value swaps commit upward
 * via `onChange` immediately. Click-outside, Esc, and the header × dismiss
 * without committing — the parent stamps `KEEP_VALUE` on every commit, so a
 * dismiss-without-mutation leaves rows untouched.
 */
export const EnumListEditor: React.FC<EnumListEditorProps> = ({
    feature,
    values,
    onChange,
    onClose,
}) => {
    const [items, setItems] = useState(
        values.map((v, i) => ({ ...v, id: v.id || `item-${i}` }))
    )
    const [showDropdown, setShowDropdown] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)

    const containerRef = useRef<HTMLDivElement>(null)
    const itemsRef = useRef(items)
    useEffect(() => {
        itemsRef.current = items
    }, [items])
    const allowedValues = feature.config?.params?.values || []

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
        }
    }

    const commit = (next: typeof items) => {
        setItems(next)
        onChange(next.map(({ id, value }) => ({ id, value })))
    }

    const updateItem = (id: string, newValue: string) => {
        commit(
            itemsRef.current.map((item) =>
                item.id === id ? { ...item, value: newValue } : item
            )
        )
        setEditingId(null)
        setShowDropdown(false)
    }

    const removeItem = (id: string) => {
        commit(itemsRef.current.filter((item) => item.id !== id))
    }

    const addItem = (value: string) => {
        const newId = `item-${Date.now()}`
        commit([...itemsRef.current, { id: newId, value }])
        setShowDropdown(false)
    }

    const startEditing = (id: string) => {
        setEditingId(id)
        setShowDropdown(true)
    }

    const getAvailableValues = (excludeId?: string) => {
        const usedValues = items
            .filter((item) => item.id !== excludeId)
            .map((item) => item.value)
        return allowedValues.filter((value: string) => !usedValues.includes(value))
    }

    return (
        <div
            ref={containerRef}
            className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-64 p-3"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">
                    Edit {feature.title}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded"
                    title="Dismiss"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="space-y-2 mb-3">
                {items.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                        <div className="flex-1 px-2 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded">
                            {item.value}
                        </div>

                        <button
                            onClick={() => startEditing(item.id!)}
                            className="p-1 text-gray-400 hover:text-amber-800 rounded"
                            title="Edit item"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                        </button>

                        <button
                            onClick={() => removeItem(item.id!)}
                            className="p-1 text-gray-400 hover:text-rose-500 rounded"
                            title="Remove item"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                ))}

                {items.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-2">
                        No items yet
                    </div>
                )}
            </div>

            <div className="relative">
                {showDropdown && !editingId ? (
                    <div className="border border-gray-300 rounded p-2 bg-gray-50">
                        <div className="text-xs text-gray-600 mb-2">Select value:</div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                            {getAvailableValues().map((value: string) => (
                                <button
                                    key={value}
                                    onClick={() => addItem(value)}
                                    className="w-full text-left px-2 py-1 text-sm hover:bg-amber-50 rounded"
                                >
                                    {value}
                                </button>
                            ))}
                            {getAvailableValues().length === 0 && (
                                <div className="text-xs text-gray-500 px-2 py-1">
                                    All values already selected
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setShowDropdown(false)}
                            className="mt-2 text-xs text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                    </div>
                ) : showDropdown && editingId ? (
                    <div className="border border-gray-300 rounded p-2 bg-gray-50">
                        <div className="text-xs text-gray-600 mb-2">Change value:</div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                            {getAvailableValues(editingId).map((value: string) => (
                                <button
                                    key={value}
                                    onClick={() => updateItem(editingId, value)}
                                    className="w-full text-left px-2 py-1 text-sm hover:bg-amber-50 rounded"
                                >
                                    {value}
                                </button>
                            ))}
                            {(() => {
                                const currentItem = items.find((item) => item.id === editingId)
                                if (currentItem) {
                                    return (
                                        <button
                                            onClick={() => {
                                                setShowDropdown(false)
                                                setEditingId(null)
                                            }}
                                            className="w-full text-left px-2 py-1 text-sm bg-amber-100 text-amber-900 rounded"
                                        >
                                            {currentItem.value} (current)
                                        </button>
                                    )
                                }
                            })()}
                        </div>
                        <button
                            onClick={() => {
                                setShowDropdown(false)
                                setEditingId(null)
                            }}
                            className="mt-2 text-xs text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowDropdown(true)}
                        disabled={getAvailableValues().length === 0}
                        className="w-full px-3 py-2 text-sm border border-dashed border-gray-300 rounded text-gray-600 hover:border-amber-300 hover:text-amber-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {getAvailableValues().length === 0 ? 'All values selected' : '+ Add value'}
                    </button>
                )}
            </div>
        </div>
    )
}
